import React from 'react';

import {assert} from 'Asserts';
import SelectTemplateCollectionItemTree from 'notebook/entry-templates/SelectTemplateCollectionItemTree/SelectTemplateCollectionItemTree';
import {TemplateCollection} from 'notebook/entry-templates/TemplateConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import ToggleGroup, {ToggleGroupOption} from 'ui-library/ToggleGroup';
import {assertNever, InputChangeEvent} from 'util/Types';

export enum ExecutionTemplateType {
  CREATE_BLANK_TEMPLATE = 'CREATE_BLANK_TEMPLATE',
  CLONE_EXISTING_TEMPLATE = 'CLONE_EXISTING_TEMPLATE',
}

export interface ConfigureExecutionTemplateModalBodyProps {
  templateCollections: Array<TemplateCollection> | null;
  templateName: string;
  setTemplateName: (newName: string) => void;
  selectedTemplateCollection: TemplateCollection | null;
  setSelectedTemplateCollection: (newTemplateCollection: TemplateCollection | null) => void;
  sourceTemplateId: string | null;
  setSourceTemplateId: (sourceTemplateId: string | null) => void;
  executionTemplateType: ExecutionTemplateType;
  setExecutionTemplateType: (executionTemplateType: ExecutionTemplateType) => void;
  toggleGroupOptions: Array<ToggleGroupOption<ExecutionTemplateType>>;
  sourceTemplateError?: string;
}

export default function ConfigureExecutionTemplateModalBody(
  props: ConfigureExecutionTemplateModalBodyProps
): JSX.Element {
  const getBody = (): JSX.Element | null => {
    if (props.templateCollections == null) {
      return <CenteredHorizontalThrobber />;
    } else {
      switch (props.executionTemplateType) {
        case ExecutionTemplateType.CREATE_BLANK_TEMPLATE:
          return (
            <ConfigureCreateBlankTemplateModal
              templateCollections={props.templateCollections}
              templateName={props.templateName}
              setTemplateName={props.setTemplateName}
              selectedTemplateCollection={props.selectedTemplateCollection}
              setSelectedTemplateCollection={props.setSelectedTemplateCollection}
            />
          );
        case ExecutionTemplateType.CLONE_EXISTING_TEMPLATE:
          return (
            <ConfigureCloneExistingTemplateModal
              templateCollections={props.templateCollections}
              templateName={props.templateName}
              setTemplateName={props.setTemplateName}
              selectedTemplateCollection={props.selectedTemplateCollection}
              setSelectedTemplateCollection={props.setSelectedTemplateCollection}
              sourceTemplateId={props.sourceTemplateId}
              setSourceTemplateId={props.setSourceTemplateId}
              sourceTemplateError={props.sourceTemplateError}
            />
          );
        default:
          assert(false, `Unexpected execution type: ${assertNever(props.executionTemplateType)}`);
      }
    }
  };

  return (
    <>
      <Row>
        <Col sm={9}>
          <ToggleGroup
            selection={props.executionTemplateType}
            size={ButtonSize.SMALL}
            isFullWidth={false}
            onChangeSelection={(selection) => {
              props.setExecutionTemplateType(selection);
            }}
            options={props.toggleGroupOptions}
          />
        </Col>
      </Row>
      {getBody()}
    </>
  );
}

interface ConfigureCreateBlankTemplateModalProps {
  templateCollections: Array<TemplateCollection>;
  templateName: string;
  setTemplateName: (newName: string) => void;
  selectedTemplateCollection: TemplateCollection | null;
  setSelectedTemplateCollection: (newTemplateCollection: TemplateCollection | null) => void;
}

export function ConfigureCreateBlankTemplateModal(
  props: ConfigureCreateBlankTemplateModalProps
): JSX.Element {
  return (
    <Row>
      <Col sm={6}>
        <FieldLabel label="Template name" />
        <TextInput
          value={props.templateName}
          onChange={(e: InputChangeEvent) => {
            props.setTemplateName(e.target.value);
          }}
          dataTestAttrs={{component: 'ConfigureCreateBlankTemplateModal', element: 'nameInputField'}}
        />
      </Col>
      <Col sm={6}>
        <FieldLabel label="Template collection" />
        <SelectDropdown
          displayName={props.selectedTemplateCollection?.name ?? 'Select a template collection'}
          disabled={false}
          menu={
            <SearchableMenu
              height={Menu.HEIGHTS.MEDIUM}
              width={Menu.WIDTHS.XLARGE}
              optionDatas={
                props.templateCollections.length === 0
                  ? [{name: 'No template collections found', value: null}]
                  : [
                      ...props.templateCollections.map((collection) => ({
                        name: collection.name,
                        value: collection,
                      })),
                    ]
              }
              onSelect={props.setSelectedTemplateCollection}
              scrollable={true}
            />
          }
          dataTestAttrs={{
            component: 'ConfigureCreateBlankTemplateModal',
            element: 'templateCollectionDropdown',
          }}
        />
      </Col>
    </Row>
  );
}

interface ConfigureCloneExistingTemplateModalProps {
  templateCollections: Array<TemplateCollection>;
  templateName: string;
  setTemplateName: (newName: string) => void;
  selectedTemplateCollection: TemplateCollection | null;
  setSelectedTemplateCollection: (newTemplateCollection: TemplateCollection | null) => void;
  sourceTemplateId: string | null;
  setSourceTemplateId: (sourceTemplateId: string | null) => void;
  sourceTemplateError?: string;
}

export function ConfigureCloneExistingTemplateModal(
  props: ConfigureCloneExistingTemplateModalProps
): JSX.Element {
  return (
    <>
      <Row>
        <Col sm={12}>
          <FormGroup dataTestAttrs={{component: 'CreateEntryFromTemplateModal', element: 'template'}}>
            <FieldLabel label="Select a template to duplicate" isRequired={false} />
            <SelectTemplateCollectionItemTree
              collectibleType="TEMPLATE"
              collectibleId={props.sourceTemplateId || ''}
              setCollectible={(template) => {
                if (template) {
                  props.setSourceTemplateId(template.id);
                } else {
                  props.setSourceTemplateId(null);
                }
              }}
              showUnpublishedCollectibles={true}
              excludeTaskSchemaExecutionTemplates={false}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <FieldLabel label="Template collection" isRequired={false} />
          <SelectDropdown
            displayName={props.selectedTemplateCollection?.name ?? 'Select a template collection'}
            disabled={false}
            menu={
              <SearchableMenu
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.XLARGE}
                optionDatas={
                  props.templateCollections.length === 0
                    ? [{name: 'No template collections found', value: null}]
                    : [
                        ...props.templateCollections.map((collection) => ({
                          name: collection.name,
                          value: collection,
                        })),
                      ]
                }
                onSelect={props.setSelectedTemplateCollection}
                scrollable={true}
              />
            }
            dataTestAttrs={{
              component: 'ConfigureCloneExistingTemplateModal',
              element: 'templateCollectionDropdown',
            }}
          />
        </Col>
        <Col sm={6}>
          <FieldLabel label="New template name" isRequired={false} />
          <TextInput
            value={props.templateName}
            onChange={(e: InputChangeEvent) => {
              props.setTemplateName(e.target.value);
            }}
            dataTestAttrs={{component: 'ConfigureCloneExistingTemplateModal', element: 'nameInputField'}}
          />
        </Col>
      </Row>
    </>
  );
}
